/**
 * Event filters functionality
 * Handles the date range, event type, and event tag filters
 */

class EventFilters {
  /**
   * @param {HTMLElement} el - The event filters container element
   */
  constructor(el) {
    this.el = el;
    
    // Initialize the component
    this.init();
  }

  /**
   * Handle view change (past/upcoming events)
   * @param {string} value - The selected view value
   */
  handleViewChange(value) {
    if (value === "") return; // Do nothing for the default option
    
    // Show loading state
    document.getElementById('event-date-filter').setAttribute('aria-busy', 'true');
    
    // Get current URL and parameters
    let url = new URL(window.location.href);
    let params = new URLSearchParams(url.search);
    
    // Update view parameter
    if (value === "past") {
      params.set('view', 'past');
    } else {
      params.delete('view');
    }
    
    // Preserve other filter parameters
    url.search = params.toString();
    
    // Announce to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('class', 'sr-only');
    announcement.textContent = 'Loading ' + (value === 'past' ? 'past' : 'upcoming') + ' events. Please wait.';
    document.body.appendChild(announcement);
    
    // Navigate to the new URL
    window.location.href = url.toString();
  }

  /**
   * Handle filter change (event type or tag)
   * @param {HTMLSelectElement} selectElement - The select element that triggered the change
   * @param {string} filterType - The type of filter (event-type or event-tag)
   */
  handleFilterChange(selectElement, filterType) {
    const value = selectElement.value;
    
    // Show loading state
    selectElement.setAttribute('aria-busy', 'true');
    
    // Get current URL and parameters
    let url = new URL(window.location.href);
    let params = new URLSearchParams(url.search);
    
    // Update filter parameter
    if (value === "") {
      params.delete(filterType);
    } else {
      params.set(filterType, value);
    }
    
    // Preserve other parameters
    url.search = params.toString();
    
    // Announce to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('class', 'sr-only');
    announcement.textContent = 'Filtering events. Please wait.';
    document.body.appendChild(announcement);
    
    // Navigate to the new URL
    window.location.href = url.toString();
  }

  /**
   * Initialize the component by setting up global handlers
   */
  init() {
    // Bind methods to maintain correct 'this' context
    const boundViewChange = this.handleViewChange.bind(this);
    const boundFilterChange = this.handleFilterChange.bind(this);
    
    // Expose methods to the global scope for inline event handlers
    window.handleViewChange = boundViewChange;
    window.handleFilterChange = boundFilterChange;
  }

  /**
   * Clean up the component
   * This method is required by ClientComponentManager for proper cleanup
   */
  destroy() {
    // No event listeners to remove since we're using inline handlers
    // But we need this method for the ClientComponentManager
  }
}

export default EventFilters;